/**
 * GatedSection Component (Web Implementation)
 *
 * Tier-gating wrapper with CSS blur teaser pattern.
 * Uses backdrop-filter for web-native blur effects.
 *
 * SECURITY NOTE: This component provides UI-level gating only.
 * Actual premium DATA must be gated server-side via RLS policies
 * or Edge Function tier checks. Use this for teasers/previews only.
 *
 * @see TEMPORAL_VS_BEHAVIORAL_ARCHITECTURE.md v1.6 - Tier-Gating UX Strategy
 */

import { View, Text, StyleSheet, Pressable } from 'react-native';
import { useTranslation } from '@dramadetector/shared';
import { useSDUIConfig } from '@dramadetector/ui/json-render';
import { useEntitlements } from '../../hooks/useEntitlements';
import { GlassButton } from '../glass/GlassButton';
import { UpgradePrompt } from './UpgradePrompt';
import { useGatedAnalytics } from './useGatedAnalytics';
import {
  TIER_HIERARCHY,
  TIER_DISPLAY_NAMES,
  type GatedSectionProps,
  type UserTier,
} from './types';

export function GatedSection({
  requiredTier,
  currentTier: propCurrentTier,
  onUpgrade,
  behavior = 'blur',
  blurIntensity = 'heavy',
  teaser,
  gatedCount,
  contentType,
  screen,
  children,
  testID,
  featureKey,
  price,
}: GatedSectionProps): React.ReactNode {
  const { t } = useTranslation('features');

  // Get current tier from hook if not provided (with error handling)
  const { tier: hookTier, error: entitlementsError } = useEntitlements();

  // Graceful degradation: default to 'free' on error or while loading
  const currentTier: UserTier = propCurrentTier ??
    (entitlementsError ? 'free' : (hookTier as UserTier) ?? 'free');

  if (entitlementsError && __DEV__) {
    console.warn('[GatedSection] Entitlements error:', entitlementsError);
  }

  // Check access
  const hasAccess = TIER_HIERARCHY[currentTier] >= TIER_HIERARCHY[requiredTier];

  // SDUI config for blur teaser (server-driven blur values).
  // Type is inferred from surface key via SDUISurfaceMap → SDUIBlurConfig.
  const { config: sduiConfig } = useSDUIConfig('blur-teaser', currentTier);

  // Analytics tracking
  const { trackBlurEngaged, trackUpgradeStarted } = useGatedAnalytics({
    contentType,
    requiredTier,
    currentTier,
    screen,
    blurIntensity,
    gatedCount,
    isGated: !hasAccess,
  });

  // User has access - render normally
  if (hasAccess) {
    return <>{children}</>;
  }

  // Redirect behavior - show upgrade prompt instead
  if (behavior === 'redirect') {
    return (
      <UpgradePrompt
        targetTier={requiredTier}
        onUpgrade={() => {
          trackUpgradeStarted('upgrade_prompt');
          onUpgrade();
        }}
        testID={testID}
        featureKey={featureKey}
        price={price}
      />
    );
  }

  // Blur behavior - render content with overlay
  const blurAmount = blurIntensity === 'heavy'
    ? sduiConfig.blurAmountHeavy
    : sduiConfig.blurAmountLight;
  const tierName = TIER_DISPLAY_NAMES[requiredTier];
  // i18n exception: SDUI teaserCopy/ctaCopy are pre-localized strings managed
  // by the content team via sdui_surface_configs. This is an intentional divergence
  // from the useTranslation() rule — when SDUI copy is null, we fall back to i18n.
  // Before adding a second locale, migrate to SDUI sending translation keys instead.
  const defaultTeaser = sduiConfig.teaserCopy
    ? sduiConfig.teaserCopy
    : gatedCount
      ? t('gating.unlock_more', { count: gatedCount })
      : t('gating.upgrade_to', { tierName });

  const handleOverlayPress = () => {
    trackBlurEngaged('tap');
  };

  const handleUpgradePress = () => {
    trackUpgradeStarted('blur_teaser');
    onUpgrade();
  };

  return (
    <View style={styles.gatedContainer} testID={testID}>
      {/* Blurred content - hidden from screen readers (WCAG 1.3.1) */}
      <View
        style={[
          styles.blurredContent,
          // Web-specific: CSS filter property
          { filter: `blur(${blurAmount}px)` } as any,
        ]}
        aria-hidden={true}
        testID={testID ? `${testID}-blurred-content` : undefined}
      >
        {children}
      </View>

      {/* Overlay with teaser - glassmorphism style */}
      <Pressable
        style={styles.overlay}
        onPress={handleOverlayPress}
        accessibilityRole="button"
        accessibilityLabel={t('gating.view_upgrade_options')}
        accessibilityHint={t('gating.tap_to_unlock', { tierName })}
        testID={testID ? `${testID}-overlay` : undefined}
      >
        <View style={styles.teaserCard}>
          <Text style={styles.teaserText}>{teaser || defaultTeaser}</Text>
          <GlassButton
            onPress={handleUpgradePress}
            testID={testID ? `${testID}-unlock-button` : undefined}
          >
            {sduiConfig.ctaCopy || t('gating.unlock_now')}
          </GlassButton>
        </View>
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  gatedContainer: {
    position: 'relative',
    overflow: 'hidden',
  },
  blurredContent: {
    // Web: pointerEvents and userSelect work via inline styles
    pointerEvents: 'none',
    userSelect: 'none',
    // @ts-expect-error - Web-specific CSS property
    WebkitUserSelect: 'none',
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    // Glassmorphism overlay
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
  },
  teaserCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.3)',
    borderRadius: 16,
    padding: 24,
    alignItems: 'center',
    maxWidth: 280,
    // Web: CSS boxShadow for glassmorphism card
    boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.15)',
  } as any,
  teaserText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1a1a2e',
    marginBottom: 16,
    textAlign: 'center',
  },
});

export default GatedSection;
